import * as React from "react"
import {
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import { useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery } from "react-query"
import { FiChevronDown } from "react-icons/fi"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"

import { BikeEntity } from "../lib/types/bike"
import { TrackerEntity } from "../lib/types/tracker"
import { fetcher } from "../lib/fetcher"
import { NoData } from "../components/NoData"
import { humanize } from "../lib/helpers/text"
import { getTrackerStateOptions } from "../lib/static/tracker"
import { useToast } from "../lib/hooks/useToast"
import { TOPBAR_HEIGHT } from "../lib/config"
import { useOnChangeOrgRedirect } from "../lib/hooks/useOnChangeOrgRedirect"
import { useMe } from "../components/providers/MeProvider"
import { isSupervisor } from "../lib/helpers/role"
import { getBikeUuidFromUrl } from "../lib/helpers/utils"
import { useOrgs } from "../components/providers/OrgProvider"
import { LocationLink } from "../components/LocationLink"
import TrackerMapMapbox from "../components/TrackerMapMapbox"
import TrackerMapGoogleMap from "../components/TrackerMapGoogleMap"
import { useMapSettings } from "../components/providers/MapSettingsProvider"

dayjs.extend(relativeTime)

export const Tracker = () => {
  useOnChangeOrgRedirect("/trackers")
  const { id } = useParams()
  const navigate = useNavigate()
  const me = useMe()
  const { currentOrgs } = useOrgs()
  const modalProps = useDisclosure()
  const toast = useToast()
  const { mapProvider } = useMapSettings()

  const { data, isLoading, refetch } = useQuery<TrackerEntity, string>(`locks/${id}/`)
  const tracker = data

  const { mutateAsync: updateTracker, isLoading: updateTrackerLoading } = useMutation<
    any,
    unknown,
    { type: string }
  >((data) => fetcher(`locks/${id}/actions/`, { body: JSON.stringify(data), method: "post" }))

  const handleUpdateState = async (type: string) => {
    try {
      const res = await updateTracker({ type })
      if (res?.detail?.non_field_errors) {
        toast({ description: res?.detail?.non_field_errors[0].message, status: "error" })
      } else if (res) {
        toast({ description: "State updated!" })
        refetch()
      } else {
        toast({ description: "Error updating state", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const bikeUuid = tracker?.bicycle ? getBikeUuidFromUrl(tracker?.bicycle) : null
  const { data: bike } = useQuery<BikeEntity, string>(`bicycles/${bikeUuid}/`, {
    enabled: !!bikeUuid,
  })
  const org = currentOrgs.find(({ url }) => url === tracker?.organization)!

  return (
    <Box overflow="scroll" h="100vh">
      {isLoading ? (
        <Center h={400}>
          <Spinner />
        </Center>
      ) : !tracker ? (
        <Center h={400}>
          <NoData>Tracker not found</NoData>
        </Center>
      ) : (
        <>
          <Flex align="center" justify="space-between" p={6} h={TOPBAR_HEIGHT}>
            <HStack spacing={6} align="center">
              <Heading as="h3" color="gray.700">
                Tracker - {tracker.bleid}
              </Heading>
            </HStack>
          </Flex>
          <Flex p={6} pt={10} w="100%">
            <Stack spacing={8} w="100%">
              <SimpleGrid spacing={8} columns={{ base: 1, md: 2 }}>
                <Stack spacing={2}>
                  <Text fontWeight="600">Info</Text>
                  {currentOrgs.length > 1 && org && (
                    <Flex align="center">
                      <Avatar mr={2} name={org.name} src={org.icon} size="xs" />
                      <Text whiteSpace="nowrap">{org.name}</Text>
                    </Flex>
                  )}

                  <Text color="gray.700">
                    Serial Number: <strong>{tracker.serial_number}</strong>
                  </Text>
                  {tracker.firmware_version ? (
                    <Text color="gray.700">
                      Firmware version: <strong>{tracker.firmware_version}</strong>
                    </Text>
                  ) : null}
                  {tracker && tracker.latitude && tracker.longitude ? (
                    <Text color="gray.700">
                      Location:{" "}
                      <strong>
                        {tracker.latitude}, {tracker.longitude}
                      </strong>
                    </Text>
                  ) : (
                    <Text color="gray.700">
                      Location: <strong>n/a</strong>
                    </Text>
                  )}
                  <a
                    href={`https://docs.google.com/forms/d/e/1FAIpQLSfS9AZUri_eEm6BLwfsUnRQaosmiKmr7DdQoagIng2qtOgm9A/viewform?usp=pp_url&entry.814125190=${
                      me.email
                    }&entry.1459092929=${me.display_name}&entry.2081821742=${
                      tracker.bleid
                    }&entry.1142341770=${bike?.name || bike?.serial_number}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="outline" size="sm">
                      Report a problem
                    </Button>
                  </a>
                </Stack>
                <Stack py={2} maxW={300} spacing={2}>
                  <Text fontWeight="600">Status</Text>
                  <Flex align="center">
                    <Text color="gray.700" fontSize="0.8rem">
                      {Math.ceil(tracker.state_of_charge || 0)}%
                    </Text>
                    <Progress
                      size="sm"
                      ml={1}
                      h="5px"
                      borderRadius="md"
                      w="100%"
                      colorScheme={
                        !tracker.state_of_charge || tracker.state_of_charge < 10
                          ? "red"
                          : tracker.state_of_charge < 50
                          ? "orange"
                          : "green"
                      }
                      value={tracker.state_of_charge || 0}
                    />
                  </Flex>
                  <Text color="gray.700" fontSize="0.8rem">
                    Last updated: {tracker.modified ? dayjs(tracker.modified).fromNow() : "N/A"}
                  </Text>
                </Stack>
                <Stack spacing={2}>
                  <Text fontWeight="600">State</Text>
                  {tracker.state === "unknown" ? (
                    "Unknown"
                  ) : (
                    <Box>
                      <Menu>
                        <MenuButton
                          as={Button}
                          size="sm"
                          minW={170}
                          isLoading={updateTrackerLoading}
                          disabled={!isSupervisor(me.roles)}
                        >
                          <Flex align="center" justify="space-between">
                            <Text>{humanize(tracker.state)}</Text>
                            <Box as={FiChevronDown} />
                          </Flex>
                        </MenuButton>
                        <MenuList>
                          {getTrackerStateOptions(tracker.state).map((opt, i) => (
                            <MenuItem
                              key={opt.value + i}
                              onClick={() => {
                                if (opt.value === "retired") {
                                  modalProps.onOpen()
                                } else {
                                  handleUpdateState(opt.value)
                                }
                              }}
                              isDisabled={updateTrackerLoading}
                            >
                              <Box as={opt.icon} boxSize="14px" mr={2} />
                              {opt.label}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                      <Modal {...modalProps} isCentered>
                        <ModalOverlay zIndex={100}>
                          <ModalContent>
                            <ModalHeader>Retire tracker</ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>Are you sure you want to retire this tracker?</ModalBody>
                            <ModalFooter>
                              <Button
                                colorScheme="red"
                                onClick={() => {
                                  handleUpdateState("retired")
                                  modalProps.onClose()
                                }}
                              >
                                Retire
                              </Button>
                            </ModalFooter>
                          </ModalContent>
                        </ModalOverlay>
                      </Modal>
                    </Box>
                  )}

                  {tracker && tracker.latitude && tracker.longitude && (
                    <LocationLink obj={{ latitude: tracker.latitude, longitude: tracker.longitude }} />
                  )}

                  {tracker && tracker.latitude && tracker.longitude && (
                    <>
                      {mapProvider === "google_maps" ? (
                        <TrackerMapGoogleMap tracker={tracker} isLoading={isLoading} />
                      ) : (
                        <TrackerMapMapbox tracker={tracker} isLoading={isLoading} />
                      )}
                    </>
                  )}
                </Stack>

                <Stack spacing={3}>
                  <Text fontWeight="600">Asset</Text>
                  {bike ? (
                    <>
                      <Text color="gray.700">
                        Connected to <strong>{bike?.name}</strong>
                      </Text>
                      <Image src={bike.image_url} w={200} objectFit="contain" />
                      <Box>
                        {bike.description && (
                          <Text color="gray.700" fontSize="0.8rem">
                            Description:{" "}
                            <Text as="span" color="gray.900" fontWeight={700}>
                              {bike.description}
                            </Text>
                          </Text>
                        )}
                      </Box>
                      <Box>
                        <Button variant="outline" size="sm" onClick={() => navigate(`/assets/${bike.uuid}`)}>
                          Go to asset
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Text>Not connected</Text>
                  )}
                </Stack>
              </SimpleGrid>
            </Stack>
          </Flex>
        </>
      )}
    </Box>
  )
}
